import type { ActivityRecord } from '../model/activity.js'
import { semicirclesToDegrees } from '../model/units.js'
import { estimated, recalculated, type SummaryValue, unavailable } from './provenance.js'
// `original` is intentionally not imported here — every value this module
// produces is derived from retained records, never copied verbatim from a
// source summary message, so none of them are ever tagged 'original'
// (that tag is the caller's to apply when it chooses the "preserve"
// strategy instead of calling into this module at all).

/**
 * Distance/ascent/descent/speed recalculation (`summary-calculation`
 * spec). Per the spec's explicit warning against "simplistic assumptions"
 * ("distance may be recorded cumulatively... and may not equal a naive
 * sum of GPS distances"), this module distinguishes two genuinely
 * different cases:
 *
 * - Records carry a device-recorded cumulative `distance` field (GPS +
 *   accelerometer/footpod fusion, scale .01m per the FIT profile) ->
 *   rebasing it to the retained range is a `recalculated` value: still
 *   device-measured data, just resummed for a smaller range.
 * - No such field exists at all -> falling back to summing GPS point
 *   positions is a geometric approximation, not a device measurement,
 *   so it is explicitly tagged `estimated`, never conflated with the
 *   device-measured case above.
 */

const EARTH_RADIUS_METERS = 6_371_000

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/** Great-circle distance between two lat/lon points, in meters. Exported for reuse by `gps-outliers.ts`. */
export function haversineMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const dLat = toRadians(b.lat - a.lat)
  const dLon = toRadians(b.lon - a.lon)
  const sinDLat = Math.sin(dLat / 2)
  const sinDLon = Math.sin(dLon / 2)
  const h =
    sinDLat * sinDLat + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * sinDLon * sinDLon
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

export function hasGps(
  r: ActivityRecord,
): r is ActivityRecord & { positionLat: number; positionLong: number } {
  return typeof r.positionLat === 'number' && typeof r.positionLong === 'number'
}

export function computeTotalDistanceMeters(records: ActivityRecord[]): SummaryValue<number> {
  const withDeviceDistance = records.filter((r) => typeof r.distance === 'number')
  if (withDeviceDistance.length >= 2) {
    const first = withDeviceDistance[0]?.distance as number
    const last = withDeviceDistance[withDeviceDistance.length - 1]?.distance as number
    return recalculated(Math.max(0, last - first))
  }

  const withGps = records.filter(hasGps)
  if (withGps.length < 2) return unavailable()

  let total = 0
  for (let i = 1; i < withGps.length; i++) {
    const prev = withGps[i - 1]
    const curr = withGps[i]
    if (prev == null || curr == null) continue
    total += haversineMeters(
      { lat: semicirclesToDegrees(prev.positionLat), lon: semicirclesToDegrees(prev.positionLong) },
      { lat: semicirclesToDegrees(curr.positionLat), lon: semicirclesToDegrees(curr.positionLong) },
    )
  }
  return estimated(total)
}

/**
 * A record's elevation in metres, preferring the higher-precision
 * `enhancedAltitude` (uint32) over the legacy `altitude` (uint16) field
 * when both are numeric. The FIT profile defines `enhancedAltitude` as
 * superseding `altitude`; some devices only write the enhanced field, so
 * reading only `altitude` made ascent/descent and exported elevation
 * silently disappear for those files. Same precedence as apps/web's
 * timeline (`readMetricValue`).
 */
export function readAltitudeMeters(r: ActivityRecord): number | undefined {
  if (typeof r.enhancedAltitude === 'number' && Number.isFinite(r.enhancedAltitude)) {
    return r.enhancedAltitude
  }
  if (typeof r.altitude === 'number' && Number.isFinite(r.altitude)) return r.altitude
  return undefined
}

/**
 * The one shared ascent/descent algorithm: sums positive (ascent) and
 * negative (descent) deltas between *consecutive numeric samples*.
 * Non-numeric entries (`null`/`undefined`/non-finite) are gaps that are
 * skipped, so the delta is taken across the gap to the next numeric
 * sample. No smoothing and no hysteresis threshold is applied — every
 * delta counts. Used by both `computeAscentDescentMeters` (record
 * elevations) and `computeAscentDescentFromElevations` (an arbitrary
 * aligned series, e.g. the terrain overlay) so the two are directly
 * comparable. Returns `undefined` when fewer than two numeric samples
 * exist.
 */
function sumAscentDescent(
  elevations: Iterable<number | null | undefined>,
): { ascent: number; descent: number } | undefined {
  let ascent = 0
  let descent = 0
  let previous: number | undefined
  let count = 0
  for (const value of elevations) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    if (previous != null) {
      const delta = value - previous
      if (delta > 0) ascent += delta
      else descent += -delta
    }
    previous = value
    count++
  }
  if (count < 2) return undefined
  return { ascent, descent }
}

/**
 * Total ascent/descent from consecutive elevation samples.
 *
 * `elevationEstimated`: set when the records' elevation came from an
 * external terrain model (a `setElevation` op is active) rather than the
 * device — the arithmetic is the same, but the inputs are a DEM surface
 * estimate, so the results are tagged `estimated`, never `recalculated`.
 */
export function computeAscentDescentMeters(
  records: ActivityRecord[],
  options?: { elevationEstimated?: boolean },
): {
  ascent: SummaryValue<number>
  descent: SummaryValue<number>
} {
  const totals = sumAscentDescent(records.map(readAltitudeMeters))
  if (totals == null) return { ascent: unavailable(), descent: unavailable() }
  const tag = options?.elevationEstimated === true ? estimated : recalculated
  return { ascent: tag(totals.ascent), descent: tag(totals.descent) }
}

/**
 * Ascent/descent from an arbitrary elevation series in metres (e.g. the
 * terrain overlay sampled per record; `null` = no sample for that
 * record). Same algorithm as `computeAscentDescentMeters` (shared
 * `sumAscentDescent`), so values computed from the same numbers are
 * identical. Always tagged `estimated`: a series supplied from outside
 * the records is not device-measured data. Fewer than two numeric
 * values -> `unavailable`.
 */
export function computeAscentDescentFromElevations(elevations: (number | null)[]): {
  ascent: SummaryValue<number>
  descent: SummaryValue<number>
} {
  const totals = sumAscentDescent(elevations)
  if (totals == null) return { ascent: unavailable(), descent: unavailable() }
  return { ascent: estimated(totals.ascent), descent: estimated(totals.descent) }
}

/** Average speed in m/s, propagating the distance value's provenance. */
export function computeAverageSpeedMetersPerSecond(
  distance: SummaryValue<number>,
  timerTimeSec: number,
): SummaryValue<number> {
  if (distance.value == null || timerTimeSec <= 0) return unavailable()
  const speed = distance.value / timerTimeSec
  return distance.provenance === 'estimated' ? estimated(speed) : recalculated(speed)
}

export function computeMaxSpeedMetersPerSecond(records: ActivityRecord[]): SummaryValue<number> {
  // enhancedSpeed (higher precision) first, like readAltitudeMeters / moving.ts's readSpeedMetersPerSecond.
  const speeds = records
    .map((r) => (typeof r.enhancedSpeed === 'number' ? r.enhancedSpeed : r.speed))
    .filter((s): s is number => typeof s === 'number')
  if (speeds.length === 0) return unavailable()
  return recalculated(Math.max(...speeds))
}

/** Average pace in seconds per kilometer — the inverse of average speed. */
export function computeAveragePaceSecPerKm(
  distance: SummaryValue<number>,
  timerTimeSec: number,
): SummaryValue<number> {
  if (distance.value == null || distance.value <= 0 || timerTimeSec <= 0) return unavailable()
  const pace = timerTimeSec / (distance.value / 1000)
  return distance.provenance === 'estimated' ? estimated(pace) : recalculated(pace)
}
